/**
 * Send a render crash to the API, which logs it into App Insights.
 *
 * Deliberately not part of base44Client: that shim mirrors the Base44 SDK and
 * keeps its shape, and crash reporting was never part of it.
 *
 * Everything here is written to fail quietly. This runs inside the error
 * boundary, on a page that has already crashed, so a throw from the reporter
 * would replace a handled crash with an unhandled one - the precise failure
 * the boundary exists to prevent.
 */

import { API_SCOPE, getAccount, initMsal, msalInstance } from '@/lib/msal';

/**
 * Attach the caller's identity when we can, and send without it when we
 * cannot. The outer boundary catches crashes in sign-in itself, where there is
 * no token to be had and the report matters most; the API accepts both.
 *
 * Deliberately not getAccessToken(): that one falls back to
 * acquireTokenRedirect when the token has expired, which navigates the browser
 * away. Doing that here would take someone straight from a crash they can read
 * to a sign-in page they did not ask for, to send a report they never see. A
 * silent token or none at all.
 */
async function authHeader() {
  try {
    await initMsal();
    const account = getAccount();
    if (!account) return {};
    const result = await msalInstance.acquireTokenSilent({ scopes: [API_SCOPE], account });
    return result?.accessToken ? { Authorization: `Bearer ${result.accessToken}` } : {};
  } catch {
    return {};
  }
}

export async function reportClientError(error, errorInfo) {
  try {
    const body = JSON.stringify({
      message: String(error?.message || error || 'Unknown error'),
      stack: error?.stack || '',
      componentStack: errorInfo?.componentStack || '',
      page: `${window.location.pathname}${window.location.search}`,
    });

    await fetch('/api/client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
      body,
      credentials: 'same-origin',
      /* The report outlives the page if the person navigates away or reloads
         to escape the crash, which is the likeliest thing for them to do. */
      keepalive: true,
    });
  } catch {
    /* Reporting is best-effort by design. The boundary has already logged the
       error to the console and is showing the person its details; losing the
       copy we send ourselves costs diagnostics, not their ability to carry on. */
  }
}
